// @flow

import React from "react";
import { localize } from "redux-i18n";
import classNames from "classnames";
import { v4 as uuid } from "uuid";

import { Button, ButtonGroup, View, ComboBox, Text } from "@components";
import type { OptionType } from "@components";
import type { AbsenceReason } from "@modules/shared/reducer/absence-reasons/types";
import type { AlertAbsenceReasonScope } from "@modules/service/reducer/alerts/types";

import styles from "./alert-absence-reasons.scss";

const ReasonScopeEnum = {
  ALL: "ALL",
  SPECIFIC_REASONS: "SPECIFIC_REASONS",
};

type AlertAbsenceReasonsObj = $Exact<{
  absenceReasons: ?Array<AlertAbsenceReasonScope>,
}>;

type OwnProps = $Exact<{
  data: AlertAbsenceReasonsObj,
  options: Array<AbsenceReason>,
  reasonsAreMandatory: boolean,
  onChange: (data: AlertAbsenceReasonsObj) => void,
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromLocalize,
}>;

type OwnState = $Exact<{
  reasonScopeType: $Values<typeof ReasonScopeEnum>,
}>;

export class AlertAbsenceReasonsForm extends React.Component<Props, OwnState> {
  state = {
    reasonScopeType: ReasonScopeEnum.ALL,
  };

  componentDidMount() {
    if (this.data && this.data.length) {
      this.setState({ reasonScopeType: ReasonScopeEnum.SPECIFIC_REASONS });
    }
  }

  get data() {
    return this.props.data.absenceReasons;
  }

  get inactiveItems() {
    return this.data
      .filter(el => !el.isActive)
      .map(el => ({
        value: el.id,
        label: el.label,
        warning: this.props.t("INACTIVE_REASON_LABEL"),
      }));
  }

  get options() {
    if (!this.props.options) {
      return [];
    }

    const options = this.props.options.map(r => ({ value: r.id, label: r.label }));
    return [...this.inactiveItems, ...options];
  }

  handleToggleRules = (reasonScope: string) => () => {
    this.setState({
      reasonScopeType: reasonScope,
    });
    this.props.onChange({ absenceReasons: [] });
  };

  handleChange = (data: number[]) => {
    const dataToUpdate = data.map(el => {
      const opt = this.props.options.find(opt => opt.id === el) || {};
      return opt;
    });

    this.props.onChange({
      absenceReasons: dataToUpdate,
    });
  };

  get selected() {
    return this.data.map(el => ({
      value: el.id,
      label: el.label,
    }));
  }

  render() {
    return (
      <div className="mb-4">
        <div className={classNames("alert-sgs__row alert-sgs__row--left")}>
          <div className="mt-4">
            <div className="small-font-size text-grey mb-2">
              {this.props.t("ALERT_DETAILS_REASON_SCOPE")}
            </div>
            {this.props.reasonsAreMandatory ? null : (
              <ButtonGroup>
                <Button
                  color={this.state.reasonScopeType === ReasonScopeEnum.ALL ? "selected" : null}
                  onClick={this.handleToggleRules(ReasonScopeEnum.ALL)}
                >
                  {this.props.t(ReasonScopeEnum.ALL)}
                </Button>
                <Button
                  color={
                    this.state.reasonScopeType === ReasonScopeEnum.SPECIFIC_REASONS
                      ? "selected"
                      : null
                  }
                  onClick={this.handleToggleRules(ReasonScopeEnum.SPECIFIC_REASONS)}
                >
                  {this.props.t(ReasonScopeEnum.SPECIFIC_REASONS)}
                </Button>
              </ButtonGroup>
            )}
          </div>
        </div>
        {this.state.reasonScopeType === ReasonScopeEnum.SPECIFIC_REASONS ||
        this.props.reasonsAreMandatory ? (
          <View margin="2rem 0" alignItems="center">
            <>
              {!!this.inactiveItems.length && (
                <Text color="negative" lineHeight={5}>
                  {this.props.t("INACITIVE_REASONS_WARNING")}
                </Text>
              )}
              <ComboBox
                value={this.selected}
                options={this.options}
                onChange={this.handleChange}
                className="w-100"
                isMulti
              />
            </>
          </View>
        ) : null}
      </div>
    );
  }
}

export default localize()(AlertAbsenceReasonsForm);
